import React, { useState, useEffect, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { formatPrice } from '../utils/formatPrice';
import { Floor, Order } from '../interfaces';
import Spinner from '../components/Spinner';
import SuccessNotification from '../components/notifications/SuccessNotification';
import DeleteConfirmation from '../components/DeleteConfirmationPopup';
import EditOrderPopup from '../components/side-panels/EditOrderPopup';
import ErrorNotification from '../components/notifications/ErrorNotification';
import VoidOrderConfirmationPopup from '../components/popups/VoidOrderConfirmationPopup';
import VoidOrderReasonPopup from '../components/popups/VoidOrderReasonPopup';
import OrdersFilters, { FilterPayload } from '../components/OrdersFilters';
import { useAuth } from '../AuthContext';
import './Orders.css';

const loadSelectedFloorId = (setSelectedFloorId: (id: string) => void, fetchFloor: (id: string) => void) => {
    const savedFloor = localStorage.getItem('selectedFloor');
    if (savedFloor) {
        const parsedFloor = JSON.parse(savedFloor);
        setSelectedFloorId(parsedFloor.value);
        fetchFloor(parsedFloor.value);
    }
};

// ── Reporting dimensions, all derived from real order fields ────────────────
type Daypart = 'breakfast' | 'lunch' | 'dinner' | 'late';
type TenderKey = 'card' | 'cash' | 'room' | 'mealplan' | 'open';
type OTypeKey = 'dinein' | 'room' | 'takeaway';
type Dim = 'type' | 'tender' | 'daypart' | 'server';

const STATUS_LABELS = ['Open', 'In progress', 'Served', 'Closed'];
const STATUS_COLORS = ['#b45309', '#2563eb', '#0891b2', '#059669'];

const DAYPARTS: { key: Daypart; label: string; range: string }[] = [
    { key: 'breakfast', label: 'Breakfast', range: '06–11' },
    { key: 'lunch', label: 'Lunch', range: '11–16' },
    { key: 'dinner', label: 'Dinner', range: '16–22' },
    { key: 'late', label: 'Late', range: '22–06' },
];

const TENDERS: { key: TenderKey; label: string; color: string }[] = [
    { key: 'card', label: 'Card', color: '#2563eb' },
    { key: 'cash', label: 'Cash', color: '#059669' },
    { key: 'room', label: 'Room charge', color: '#7c3aed' },
    { key: 'mealplan', label: 'Meal Plan', color: '#0d9488' },
    { key: 'open', label: 'Unpaid', color: '#b45309' },
];

const OTYPES: { key: OTypeKey; label: string; color: string }[] = [
    { key: 'dinein', label: 'Dine-in', color: 'oklch(0.484 0.236 269)' },
    { key: 'room', label: 'Room service', color: '#7c3aed' },
    { key: 'takeaway', label: 'Takeaway', color: '#0891b2' },
];

const daypartOf = (d: Date | string | undefined): Daypart => {
    const h = d ? new Date(d).getHours() : 0;
    if (h >= 6 && h < 11) return 'breakfast';
    if (h >= 11 && h < 16) return 'lunch';
    if (h >= 16 && h < 22) return 'dinner';
    return 'late';
};

const TENDER_ALIASES: Record<string, TenderKey> = {
    room_charge: 'room',
    meal_plan: 'mealplan',
};

// An order settled entirely by a meal plan carries one payment of type meal_plan for
// 0.00 — the balance is cleared by the guest's package, not left owing. It used to fall
// through to 'open' and render as "Unpaid", which misreported closed revenue.
const tenderOf = (o: Order): TenderKey => {
    const ps = o.payments || [];
    if (ps.length === 0) return 'open';
    const sums: Record<string, number> = {};
    ps.forEach((p) => {
        const t = TENDER_ALIASES[p.type] || p.type;
        sums[t] = (sums[t] || 0) + (p.amount || 0);
    });
    // Highest-value tender wins, so a part-meal-plan / part-room-charge order reads as the
    // tender that actually moved money. Meal-plan-only orders sum to 0 and still resolve to
    // 'mealplan' because it is the sole entry.
    const top = Object.entries(sums).sort((a, b) => b[1] - a[1])[0];
    const key = top ? top[0] : 'open';
    return (['card', 'cash', 'room', 'mealplan'].includes(key) ? key : 'open') as TenderKey;
};

const typeOf = (o: Order): OTypeKey => {
    if (o.roomNumber || o.prostayReservationNumber || (o.orderRoomAllocations?.length || 0) > 0) return 'room';
    if (o.tableId || o.tableName) return 'dinein';
    return 'takeaway';
};

const netOf = (o: Order): number => (o.subtotal != null ? o.subtotal : (o.total || 0) - (o.tax || 0));

const Orders: React.FC = () => {
    const { hasPerm } = useAuth();
    const [, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
    const [orders, setOrders] = useState<Order[]>([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const orderStatuses = ["Submit", "In Progress", "Served", "Closed/Done"];
    const [orderIdToDelete, setorderIdToDelete] = useState<string | null>(null);
    const [headerTitle, setHeaderTitle] = useState('');
    const [content, setContent] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [titleError, setTitleError] = useState('');
    const [contentError, setContentError] = useState('');
    const [showError, setShowError] = useState(false);
    const [orderToEdit, setOrderToEdit] = useState<Order | null>(null);
    const [floor, setFloor] = useState<Floor | null>(null);
    const [displayVoidConfirmation, setDisplayVoidConfirmation] = useState(false);
    const [displayVoidReason, setDisplayVoidReason] = useState(false);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    // Print / PDF preview. The order snapshot is frozen when the preview opens so a
    // background refresh (the list polls) can't change the document under the user
    // between seeing it and saving it.
    const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState(false);
    const [previewOrders, setPreviewOrders] = useState<Order[] | null>(null);
    const [previewStamp, setPreviewStamp] = useState<Date | null>(null);
    const [isSavingPdf, setIsSavingPdf] = useState(false);
    const previewDocRef = useRef<HTMLDivElement>(null);
    const [allFloors, setAllFloors] = useState<Floor[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [filters, setFilters] = useState({
        floorId: null as string | null,
        status: null as number | null,
        customerName: '',
        orderNumber: '',
        minAmount: '',
        maxAmount: ''
    });

    // Reporting dashboard state (layered on top of the search/side-panel filters)
    const [daypartF, setDaypartF] = useState<Daypart | null>(null);
    const [statusF, setStatusF] = useState<number | null>(null);
    const [tenderF, setTenderF] = useState<TenderKey | null>(null);
    const [typeF, setTypeF] = useState<OTypeKey | null>(null);
    const [dim, setDim] = useState<Dim>('tender');

    const getActiveFiltersCount = () => {
        let count = 0;
        if (filters.floorId) count++;
        if (filters.status !== null) count++;
        if (filters.customerName) count++;
        if (filters.orderNumber) count++;
        if (filters.minAmount) count++;
        if (filters.maxAmount) count++;
        return count;
    };

    const filteredOrders = orders.filter((order) => {
        // Hide phantom empty orders. Opening/viewing a table creates an open order with
        // "In Progress" status even before anything is added, but the floor plan only marks a
        // table red when it has actual items. Such an order has no transaction, so it must not
        // show up as "In Progress" here — keeping the list in sync with the (green) table color.
        const hasItems = (order.products?.length ?? 0) > 0 || (order.combinations?.length ?? 0) > 0;
        if (!hasItems && order.status !== 3) return false;

        // Search term filter (across email, name, order number, table, room)
        const search = searchTerm.toLowerCase();
        const tableName = order.tableName || getTableNoById(order.tableId) || '';
        const matchesSearch = !searchTerm ||
            order.customerEmail?.toLowerCase().includes(search) ||
            order.customerName?.toLowerCase().includes(search) ||
            order.orderNumber?.toLowerCase().includes(search) ||
            tableName.toLowerCase().includes(search) ||
            order.roomNumber?.toLowerCase().includes(search);

        // Floor filter
        const matchesFloor = !filters.floorId ||
            (floor && floor.tables?.some(t => t.id === order.tableId));

        // Status filter
        const matchesStatus = filters.status === null || order.status === filters.status;

        // Customer name filter
        const matchesCustomerName = !filters.customerName ||
            order.customerName?.toLowerCase().includes(filters.customerName.toLowerCase());

        // Order number filter
        const matchesOrderNumber = !filters.orderNumber ||
            order.orderNumber?.toLowerCase().includes(filters.orderNumber.toLowerCase());

        // Amount filters
        const minAmount = filters.minAmount ? parseFloat(filters.minAmount) : null;
        const maxAmount = filters.maxAmount ? parseFloat(filters.maxAmount) : null;
        const matchesMinAmount = minAmount === null || order.total >= minAmount;
        const matchesMaxAmount = maxAmount === null || order.total <= maxAmount;

        return matchesSearch && matchesFloor && matchesStatus && matchesCustomerName &&
            matchesOrderNumber && matchesMinAmount && matchesMaxAmount;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Dashboard layer: daypart / status / tender / type on top of filteredOrders
    const visible = filteredOrders.filter((o) =>
        (daypartF == null || daypartOf(o.date) === daypartF) &&
        (statusF == null || o.status === statusF) &&
        (tenderF == null || tenderOf(o) === tenderF) &&
        (typeF == null || typeOf(o) === typeF)
    );

    const dashFiltered = daypartF != null || statusF != null || tenderF != null || typeF != null;

    const summary = (() => {
        const live = visible.filter((o) => !o.isVoided);
        const voided = visible.filter((o) => o.isVoided);
        const grossSum = live.reduce((a, o) => a + (o.total || 0), 0);
        const net = live.reduce((a, o) => a + netOf(o), 0);
        const tax = live.reduce((a, o) => a + (o.tax || 0), 0);
        const disc = live.reduce((a, o) => a + (o.discount || 0), 0);
        const voidsTotal = voided.reduce((a, o) => a + (o.total || 0), 0);
        const count = live.length;
        return { grossSum, net, tax, disc, voidsTotal, voidCount: voided.length, count, avg: count ? grossSum / count : 0 };
    })();

    // Breakdown distribution for the current pivot dimension (non-voided)
    const dist = (() => {
        const live = visible.filter((o) => !o.isVoided);
        let src: { key: string; label: string; color: string }[];
        let pick: (o: Order) => string;
        if (dim === 'type') { src = OTYPES as any; pick = (o) => typeOf(o); }
        else if (dim === 'tender') { src = TENDERS as any; pick = (o) => tenderOf(o); }
        else if (dim === 'daypart') { src = DAYPARTS.map((d) => ({ key: d.key, label: d.label, color: '#64748b' })); pick = (o) => daypartOf(o.date); }
        else {
            const servers = Array.from(new Set(live.map((o) => o.waiter).filter(Boolean)));
            src = servers.map((s) => ({ key: s, label: s, color: '#64748b' }));
            pick = (o) => o.waiter;
        }
        const rows = src
            .map((s) => {
                const items = live.filter((o) => pick(o) === s.key);
                return { key: s.key, label: s.label, color: s.color, count: items.length, total: items.reduce((a, o) => a + (o.total || 0), 0) };
            })
            .filter((r) => r.count > 0)
            .sort((a, b) => b.total - a.total);
        const max = Math.max(1, ...rows.map((r) => r.total));
        return { rows, max };
    })();

    const totalPages = Math.max(1, Math.ceil(visible.length / itemsPerPage));
    const paginatedOrders = visible.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // Reset to page 1 when filters or search change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filters, itemsPerPage, daypartF, statusF, tenderF, typeF]);

    const clearDashFilters = () => { setDaypartF(null); setStatusF(null); setTenderF(null); setTypeF(null); };

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getTableNoById = (tableId: string): string | null => {
        if (floor && floor.tables) {
            const table = floor.tables.find((t) => t.id === tableId);
            return table ? table.tableNo : null;
        }
        return null;
    };

    const fetchFloor = async (floorId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Floor/` + floorId, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Error fetching floor');
            }
            const data = await response.json();
            console.log(data);
            setFloor(data);
        } catch (error) {
            console.error("Error fetching data: ", error);
        }
    };

    const fetchAllFloors = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Floor`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Error fetching floors');
            }
            const data = await response.json();
            setAllFloors(data);
        } catch (error) {
            console.error("Error fetching floors: ", error);
        }
    };

    const handleFiltersApply = (newFilters: FilterPayload) => {
        setFilters({
            floorId: newFilters.floorId,
            status: newFilters.status,
            customerName: newFilters.customerName || '',
            orderNumber: newFilters.orderNumber || '',
            minAmount: newFilters.minAmount?.toString() || '',
            maxAmount: newFilters.maxAmount?.toString() || ''
        });

        // If a specific floor is selected, fetch that floor's data
        if (newFilters.floorId && newFilters.floorId !== selectedFloorId) {
            fetchFloor(newFilters.floorId);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const clearFilters = () => {
        setFilters({
            floorId: null,
            status: null,
            customerName: '',
            orderNumber: '',
            minAmount: '',
            maxAmount: ''
        });
    };

    useEffect(() => {
        fetchAllFloors();
    }, []);

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'selectedFloor') {
                loadSelectedFloorId(setSelectedFloorId, fetchFloor);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    useEffect(() => {
        loadSelectedFloorId(setSelectedFloorId, fetchFloor);
    }, []);

    useEffect(() => {
        if (selectedFloorId) {
            fetchFloor(selectedFloorId);
        }
    }, [selectedFloorId]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const fetchOrders = useCallback(async () => {
        try {
            if (!floor?.id) return;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/Order/get-all-today/${floor.id}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setOrders(data);
        } catch (error) {
            console.error("Error fetching orders: ", error);
        }
    }, [floor?.id]);

    useEffect(() => {
        const fetchData = async () => {
            setShowSpinner(true);
            await fetchOrders();
            setShowSpinner(false);
        };
        fetchData();
    }, [fetchOrders]);

    const handleRefresh = async () => {
        setShowSpinner(true);
        await fetchOrders();
        setShowSpinner(false);
    }

    /**
     * Opens the order list preview.
     *
     * This used to `window.open('', '_blank')` and write a document that called
     * window.print() on load. Inside the Capacitor WebView that lands the user in a
     * chromeless view with no back, no close and no reliable print dialog — the trap
     * screen QA reported, escapable only by force-quitting the app. The preview is now
     * an in-app overlay, so the app's own action bar is always there to leave by.
     */
    const handlePrintOrderList = () => {
        setPreviewOrders(filteredOrders);
        setPreviewStamp(new Date());
        setIsPrintPreviewOpen(true);
    };

    const closePrintPreview = () => {
        setIsPrintPreviewOpen(false);
        setPreviewOrders(null);
    };

    // Escape closes the preview on laptops. Android's hardware back is handled globally in
    // App.tsx (history.back, or exitApp when there is nothing to go back to), so it would
    // leave the Orders page entirely rather than dismiss this — hence the always-visible
    // Back / Close controls in the action bar, which are the reliable way out on a tablet.
    useEffect(() => {
        if (!isPrintPreviewOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePrintPreview(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isPrintPreviewOpen]);

    const handleDownloadOrderListPdf = async () => {
        if (!previewDocRef.current) return;
        try {
            setIsSavingPdf(true);
            const canvas = await html2canvas(previewDocRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });

            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();

            // Scale to the page width, then walk down the canvas a page at a time so a
            // long list becomes a multi-page PDF instead of being cropped to page one.
            const imgW = pageW;
            const imgH = (canvas.height * imgW) / canvas.width;
            const imgData = canvas.toDataURL('image/png');

            let remaining = imgH;
            let offset = 0;
            pdf.addImage(imgData, 'PNG', 0, 0, imgW, imgH);
            remaining -= pageH;
            while (remaining > 0) {
                offset -= pageH;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, offset, imgW, imgH);
                remaining -= pageH;
            }

            const stamp = (previewStamp ?? new Date()).toISOString().slice(0, 10);
            pdf.save(`Order-List_${stamp}.pdf`);
        } catch (error) {
            console.error('Failed to save the order list PDF:', error);
        } finally {
            setIsSavingPdf(false);
        }
    };

    // Void and delete can be refused by the server - Prostay will not let a charge move once
    // the guest has checked out - and the refusal names what the waiter has to do instead. Show
    // that sentence rather than a generic failure, which reads as "try again" for something no
    // amount of retrying will fix.
    const readServerMessage = async (response: Response, fallback: string) => {
        try {
            const body = await response.json();
            if (body && typeof body.message === 'string' && body.message.trim()) return body.message;
        } catch {
            // not JSON
        }
        return fallback;
    };

    const handleDelete = async () => {
        try {
            const id = orderIdToDelete;
            setorderIdToDelete(null);
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/delete/` + id, {
                method: 'GET',
                credentials: 'include',
            });
            if (response.ok) {
                setHeaderTitle("Success!");
                setContent("Order was deleted Successfully!");
                setShowSuccess(true);
                await fetchOrders();
                setShowSpinner(false);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            } else {
                setShowSpinner(false);
                setTitleError("Order not deleted");
                setContentError(await readServerMessage(response, "The order could not be deleted. Please try again."));
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                }, 6000);
            }
        } catch (error) {
            setShowSpinner(false);
            setTitleError("Order not deleted");
            setContentError("There was a problem while deleting the order. Please try again.");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
    }


    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    const handleVoidReasonSelected = async (reason: string) => {
        try {
            setDisplayVoidReason(false);
            setShowSpinner(true);
            const id = orderToEdit?.id;
            setOrderToEdit(null);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/void-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderId: id,
                    voidReason: reason
                }),
                credentials: 'include',
            });
            if (response.ok) {
                setHeaderTitle("Success!");
                setContent("Order voided successfully.");
                setShowSuccess(true);
                await fetchOrders();
                setShowSpinner(false);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            } else {
                setShowSpinner(false);
                setTitleError("Order not voided");
                setContentError(await readServerMessage(response, "The order could not be voided. Please try again."));
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                }, 6000);
            }
        } catch (error) {
            setShowSpinner(false);
            setTitleError("Error!");
            setContentError("There was a problem while voiding the order. Please try again.");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
    }

    const handleUpdateOrder = async (updatedOrder: Order) => {
        setOrderToEdit(null);
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: updatedOrder.id,
                    propertyId: updatedOrder.propertyId,
                    products: updatedOrder.products,
                    customerEmail: updatedOrder.customerEmail,
                    customerName: updatedOrder.customerName,
                    customerId: updatedOrder.customerId,
                    seats: updatedOrder.seats,
                    currency: updatedOrder.currency,
                    subtotal: updatedOrder.subtotal,
                    total: updatedOrder.total,
                    tax: updatedOrder.tax,
                    isTaxPercentage: updatedOrder.isTaxPercentage,
                    discount: updatedOrder.discount,
                    isOpen: updatedOrder.isOpen,
                    tableId: updatedOrder.tableId,
                    tableName: updatedOrder.tableName,
                    orderDiscountType: updatedOrder.orderDiscountType,
                    orderDiscountValue: updatedOrder.orderDiscountValue,
                    status: updatedOrder.status,
                    orderNumber: updatedOrder.orderNumber,
                    waiter: updatedOrder.waiter,
                    date: updatedOrder.date,
                    closedBy: updatedOrder.closedBy,
                    createdBy: updatedOrder.createdBy,
                    orderPaidOn: updatedOrder.orderPaidOn,
                    subtotalWithoutDiscounts: updatedOrder.subtotalWithoutDiscounts
                }),
                credentials: 'include',
            });
            if (response) {
                setHeaderTitle("Success!");
                setContent("Order updated successfully.");
                setShowSuccess(true);
                await fetchOrders();
                setShowSpinner(false);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            }
        } catch (error) {
            setShowSpinner(false);
            setTitleError("Error!");
            setContentError("There was a problem while saving the order. Please try again.");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
    }

    const statusCounts = STATUS_LABELS.map((_, i) => filteredOrders.filter((o) => o.status === i && !o.isVoided).length);
    const tenderTotals = TENDERS.map((t) => ({
        ...t,
        total: filteredOrders.filter((o) => tenderOf(o) === t.key && !o.isVoided).reduce((a, o) => a + (o.total || 0), 0),
    }));
    const tenderGrand = Math.max(1, tenderTotals.reduce((a, t) => a + t.total, 0));

    const fmtTime = (d?: Date | string) => (d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');

    return (
        <div className="orders-view ov2 ps-scope">
            <header className="ov2-head">
                <div className="ov2-head__lead">
                    <h1 className="ov2-title">Orders</h1>
                    <span className="ov2-today">Today · {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}{floor?.floorName ? ` · ${floor.floorName}` : ''}</span>
                </div>
                <div className="ov2-head__tools">
                    <button className="ov2-tool" onClick={handleRefresh}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M3 10a9 9 0 0 1 15-3.7L21 9M21 5v4h-4M21 14a9 9 0 0 1-15 3.7L3 15m0 4v-4h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        Refresh
                    </button>
                    <button className="ov2-tool" onClick={() => setIsFiltersOpen(true)}>
                        <svg width="16" height="14" viewBox="0 0 18 12" fill="none"><path d="M4 6H14M1.5 1H16.5M6.5 11H11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        {getActiveFiltersCount() > 0 ? `Filters (${getActiveFiltersCount()})` : 'Filters'}
                    </button>
                    <button className="ov2-tool" onClick={handlePrintOrderList}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6v-8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        Print / PDF
                    </button>
                    <div className="ov2-search">
                        <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><path d="M16.5 16.5L13.5834 13.5833M15.6667 8.58333C15.6667 12.4954 12.4954 15.6667 8.58333 15.6667C4.67132 15.6667 1.5 12.4954 1.5 8.58333C1.5 4.67132 4.67132 1.5 8.58333 1.5C12.4954 1.5 15.6667 4.67132 15.6667 8.58333Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        <input type="text" placeholder="Search orders" value={searchTerm} onInput={handleSearch} />
                    </div>
                </div>
            </header>

            {/* Live summary — recomputes for whatever is in view */}
            <div className="ov2-stats">
                <div className="ov2-stat ov2-stat--hero">
                    <span className="ov2-stat__k">Gross sales{dashFiltered ? ' (filtered)' : ''}</span>
                    <span className="ov2-stat__v">{formatPrice(summary.grossSum)}</span>
                </div>
                <div className="ov2-stat"><span className="ov2-stat__k">Net</span><span className="ov2-stat__v">{formatPrice(summary.net)}</span></div>
                <div className="ov2-stat"><span className="ov2-stat__k">Tax</span><span className="ov2-stat__v">{formatPrice(summary.tax)}</span></div>
                <div className="ov2-stat"><span className="ov2-stat__k">Discounts</span><span className="ov2-stat__v ov2-pos">−{formatPrice(summary.disc)}</span></div>
                <div className="ov2-stat"><span className="ov2-stat__k">Voids ({summary.voidCount})</span><span className="ov2-stat__v ov2-danger">{formatPrice(summary.voidsTotal)}</span></div>
                <div className="ov2-stat"><span className="ov2-stat__k">Avg check</span><span className="ov2-stat__v">{formatPrice(summary.avg)}</span></div>
            </div>

            {/* Daypart + status filters */}
            <div className="ov2-filters">
                <div className="ov2-dayparts">
                    <button className={`ov2-dp${daypartF === null ? ' is-active' : ''}`} onClick={() => setDaypartF(null)}>All day</button>
                    {DAYPARTS.map((d) => (
                        <button key={d.key} className={`ov2-dp${daypartF === d.key ? ' is-active' : ''}`} onClick={() => setDaypartF(daypartF === d.key ? null : d.key)}>
                            {d.label}<span className="ov2-dp__r">{d.range}</span>
                        </button>
                    ))}
                </div>
                <div className="ov2-statuschips">
                    {STATUS_LABELS.map((s, i) => (
                        <button key={s} className={`ov2-chip${statusF === i ? ' is-active' : ''}`} style={{ ['--c' as any]: STATUS_COLORS[i] }} onClick={() => setStatusF(statusF === i ? null : i)}>
                            {s}<span className="ov2-chip__n">{statusCounts[i]}</span>
                        </button>
                    ))}
                    {dashFiltered && <button className="ov2-clear" onClick={clearDashFilters}>Clear</button>}
                </div>
            </div>

            <div className="ov2-main">
                {/* Breakdown panel */}
                <section className="ov2-panel">
                    <div className="ov2-panel__head">
                        <span className="ov2-panel__title">Breakdown</span>
                        <div className="ov2-dimseg">
                            {(['tender', 'type', 'daypart', 'server'] as Dim[]).map((d) => (
                                <button key={d} className={`ov2-dimbtn${dim === d ? ' is-active' : ''}`} onClick={() => setDim(d)}>
                                    {d === 'type' ? 'Type' : d === 'tender' ? 'Tender' : d === 'daypart' ? 'Daypart' : 'Server'}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="ov2-bars">
                        {dist.rows.map((r) => (
                            <div key={r.key} className="ov2-bar">
                                <div className="ov2-bar__top">
                                    <span className="ov2-bar__label"><span className="ov2-bar__dot" style={{ background: r.color }} />{r.label}</span>
                                    <span className="ov2-bar__val">{formatPrice(r.total)}<i>{r.count}</i></span>
                                </div>
                                <span className="ov2-bar__track"><span style={{ width: `${(r.total / dist.max) * 100}%`, background: r.color }} /></span>
                            </div>
                        ))}
                        {dist.rows.length === 0 && <div className="ov2-empty">No sales in view.</div>}
                    </div>
                    <div className="ov2-panel__foot">
                        <span>Total {dashFiltered ? '(filtered)' : 'today'}</span>
                        <b>{formatPrice(summary.grossSum)} · {summary.count}</b>
                    </div>
                </section>

                {/* Orders table */}
                <section className="ov2-tablewrap">
                    <div className="ov2-tenderchips">
                        {tenderTotals.map((t) => (
                            <button key={t.key} className={`ov2-tchip${tenderF === t.key ? ' is-active' : ''}`} style={{ ['--c' as any]: t.color }} onClick={() => setTenderF(tenderF === t.key ? null : t.key)}>
                                <span className="ov2-tchip__top">
                                    <span className="ov2-tchip__dot" />
                                    <span className="ov2-tchip__label">{t.label}</span>
                                    <b>{formatPrice(t.total)}</b>
                                </span>
                                <span className="ov2-tchip__bar"><span style={{ width: `${(t.total / tenderGrand) * 100}%` }} /></span>
                            </button>
                        ))}
                    </div>
                    <div className="ov2-table">
                        <div className="ov2-row ov2-row--head">
                            <span>Order</span><span>Customer</span><span>Table</span><span>Server</span><span>Time</span><span>Tender</span><span className="ov2-r">Amount</span><span>Status</span><span className="ov2-r">·</span>
                        </div>
                        {paginatedOrders.map((order) => {
                            const tk = tenderOf(order);
                            const tm = TENDERS.find((t) => t.key === tk)!;
                            return (
                                <div key={order.id} className={`ov2-row${order.isVoided ? ' is-void' : ''}`}>
                                    <span className="ov2-mono"><b>{order.orderNumber}</b></span>
                                    <span className="ov2-trunc">{order.customerName || order.roomNumber || 'Walk-in'}</span>
                                    <span>{order.tableName || getTableNoById(order.tableId) || '—'}</span>
                                    <span className="ov2-trunc">{order.waiter || '—'}</span>
                                    <span className="ov2-mono ov2-dim">{fmtTime(order.date)}</span>
                                    <span>{order.isVoided ? <em className="ov2-voidtag">Voided</em> : <span className="ov2-tt" style={{ color: tm.color }}>{tm.label}</span>}</span>
                                    <span className="ov2-r ov2-mono"><b>{formatPrice(order.total)}</b></span>
                                    <span>{order.isVoided
                                        ? <span className="ov2-st ov2-st--void">Void</span>
                                        : <span className={`ov2-st ov2-st--${order.status}`}>{STATUS_LABELS[order.status] || orderStatuses[order.status]}</span>}</span>
                                    <span className="ov2-r ov2-actions">
                                        {hasPerm('pos-app.order.delete') && (
                                            <button className="ov2-iconbtn" title="Delete" onClick={() => setorderIdToDelete(order.id)}>
                                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M13.3333 4.99935V4.33268C13.3333 3.39926 13.3333 2.93255 13.1517 2.57603C12.9919 2.26243 12.7369 2.00746 12.4233 1.84767C12.0668 1.66602 11.6001 1.66602 10.6667 1.66602H9.33333C8.39991 1.66602 7.9332 1.66602 7.57668 1.84767C7.26308 2.00746 7.00811 2.26243 6.84832 2.57603C6.66667 2.93255 6.66667 3.39926 6.66667 4.33268V4.99935M8.33333 9.58268V13.7493M11.6667 9.58268V13.7493M2.5 4.99935H17.5M15.8333 4.99935V14.3327C15.8333 15.7328 15.8333 16.4329 15.5608 16.9677C15.3212 17.4381 14.9387 17.8205 14.4683 18.0602C13.9335 18.3327 13.2335 18.3327 11.8333 18.3327H8.16667C6.76654 18.3327 6.06647 18.3327 5.53169 18.0602C5.06129 17.8205 4.67883 17.4381 4.43915 16.9677C4.16667 16.4329 4.16667 15.7328 4.16667 14.3327V4.99935" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </button>
                                        )}
                                        <button className="ov2-iconbtn" title="Edit" onClick={() => setOrderToEdit(order)}>
                                            <svg width="16" height="16" viewBox="0 0 19 19" fill="none"><path d="M1.39637 15.0973C1.43466 14.7527 1.4538 14.5804 1.50594 14.4194C1.55219 14.2765 1.61753 14.1405 1.70021 14.0152C1.79339 13.8739 1.91596 13.7513 2.16112 13.5061L13.1664 2.5009C14.0868 1.58043 15.5792 1.58043 16.4997 2.5009C17.4202 3.42138 17.4202 4.91376 16.4997 5.83424L5.49445 16.8395C5.2493 17.0846 5.12672 17.2072 4.98541 17.3004C4.86005 17.383 4.72408 17.4484 4.58121 17.4946C4.42018 17.5468 4.24789 17.5659 3.90331 17.6042L1.08301 17.9176L1.39637 15.0973Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </button>
                                    </span>
                                </div>
                            );
                        })}
                        {visible.length === 0 && <div className="ov2-empty">No orders match this filter.</div>}
                    </div>

                    {visible.length > 0 && (
                        <div className="ov2-pager">
                            <span className="ov2-pager__info">
                                {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, visible.length)} of {visible.length}
                            </span>
                            <div className="ov2-pager__ctrls">
                                <select className="ov2-pager__select" value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                                    <option value={10}>10 / page</option>
                                    <option value={50}>50 / page</option>
                                    <option value={100}>100 / page</option>
                                </select>
                                <button className="ov2-pager__btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>&#8249;</button>
                                <span className="ov2-pager__page">{currentPage} / {totalPages}</span>
                                <button className="ov2-pager__btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>&#8250;</button>
                            </div>
                        </div>
                    )}
                </section>
            </div>
            <div style={{ paddingBottom: 100 }} />
            {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            {showError && <ErrorNotification headerTitle={titleError} content={contentError} />}

            {showSpinner && (
                <Spinner />
            )}
            {
                orderIdToDelete &&
                <>
                    <div className='popup-overlay' onClick={() => setorderIdToDelete(null)}>
                    </div>
                    <DeleteConfirmation title='Delete Confirmation' content='Are you sure that you want to delete this item?' onClose={() => setorderIdToDelete(null)} onDelete={handleDelete} />
                </>
            }
            {
                orderToEdit &&
                <>
                    <EditOrderPopup onShowVoidReason={() => setDisplayVoidReason(true)} onShowVoidConfirmation={() => setDisplayVoidConfirmation(true)} tables={floor?.tables} isOpen={orderToEdit != null} order={orderToEdit} onClose={() => setOrderToEdit(null)} onSave={handleUpdateOrder} receiptPrinterId={floor?.receiptPrinterId} />
                </>
            }
            {
                displayVoidConfirmation &&
                <>
                    <div className='dark-popup-overlay' style={{ zIndex: 999 }}>
                    </div>
                    <VoidOrderConfirmationPopup onClose={() => setDisplayVoidConfirmation(false)} onConfirm={() => { setDisplayVoidConfirmation(false); setDisplayVoidReason(true); }} />
                </>
            }
            {
                displayVoidReason &&
                <>
                    <div className='dark-popup-overlay' style={{ zIndex: 999 }}>
                    </div>
                    <VoidOrderReasonPopup onClose={() => setDisplayVoidReason(false)} onReasonSelected={handleVoidReasonSelected} />
                </>
            }
            <OrdersFilters
                isOpen={isFiltersOpen}
                onClose={() => setIsFiltersOpen(false)}
                onSave={handleFiltersApply}
                floorOptions={allFloors.map(f => ({ value: f.id, label: f.floorName }))}
                currentFilters={filters}
            />

            {isPrintPreviewOpen && previewOrders && (
                <div className='op-preview' role='dialog' aria-modal='true' aria-label='Order list preview'>
                    {/* Always-present action bar. The previous implementation handed the document
                        to a bare popup window, which on Android left no way back into the app. */}
                    <div className='op-preview__bar'>
                        <button className='op-preview__back' onClick={closePrintPreview}>
                            <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                                <path d='M19 12H5m0 0 7 7m-7-7 7-7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                            </svg>
                            Back to orders
                        </button>
                        <span className='op-preview__title'>Order List preview</span>
                        <div className='op-preview__actions'>
                            <button className='op-preview__btn' onClick={() => window.print()}>
                                <svg width='16' height='16' viewBox='0 0 24 24' fill='none'><path d='M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6v-8z' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>
                                Print
                            </button>
                            <button className='op-preview__btn op-preview__btn--primary' onClick={handleDownloadOrderListPdf} disabled={isSavingPdf}>
                                <svg width='16' height='16' viewBox='0 0 24 24' fill='none'><path d='M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>
                                {isSavingPdf ? 'Saving…' : 'Download PDF'}
                            </button>
                            <button className='op-preview__close' onClick={closePrintPreview} aria-label='Close preview'>
                                <svg width='16' height='16' viewBox='0 0 24 24' fill='none'><path d='M18 6 6 18M6 6l12 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>
                            </button>
                        </div>
                    </div>

                    <div className='op-preview__scroll'>
                        <div className='op-doc' ref={previewDocRef}>
                            <div className='op-doc__head'>
                                <h1>Order List</h1>
                                <div className='op-doc__meta'>
                                    {floor?.floorName || 'All Floors'}<br />
                                    {(previewStamp ?? new Date()).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    {' at '}
                                    {(previewStamp ?? new Date()).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}<br />
                                    {previewOrders.length} order{previewOrders.length !== 1 ? 's' : ''}
                                </div>
                            </div>
                            <table className='op-doc__table'>
                                <thead>
                                    <tr>
                                        <th>Order #</th><th>Customer</th><th>Table</th>
                                        <th className='op-doc__num'>Amount</th><th>Status</th><th>Waiter</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewOrders.map((order, i) => (
                                        <tr key={order.id ?? i} style={{ background: i % 2 === 0 ? '#F9FAFB' : '#fff' }}>
                                            <td>{order.orderNumber || ''}</td>
                                            <td>{order.customerName || ''}</td>
                                            <td>{order.tableName || getTableNoById(order.tableId) || ''}</td>
                                            <td className='op-doc__num'>{formatPrice(order.total)}</td>
                                            <td>{orderStatuses[order.status] || ''}</td>
                                            <td>{order.waiter || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className='op-doc__summary'>
                                Grand Total: <strong>{formatPrice(previewOrders.reduce((sum, o) => sum + (o.total || 0), 0))}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Orders;